import { Fragment, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getFinanceTransactions, getOrders, type FinanceTransaction, type OrderLine } from '../api.js';
import { Button, EmptyState, Group, IconTile, MoreRow, Row, Segmented, Sheet } from '../ui/index.js';
import { CategoryPickerBody } from './CategoryPicker.js';
import { dayLabel, isUncategorized, money, shortDate } from './format.js';
import { RANGES, rangeOf, SearchField, SkeletonGroup, type RangeKey } from './parts.js';
import Review from './Review.js';
import SplitEditor, { splitSummary } from './SplitEditor.js';
import { useCategoryTree } from './tree.js';

type View = 'review' | 'all';

/**
 * Transactions — Review (the uncategorized rows, one at a time) and the
 * ledger itself, behind one switch. `?view=` picks; without it, Review when
 * anything is left to sort.
 */
export default function Transactions({
  reviewCount,
  onReviewCount,
  onImport,
  onChange,
}: {
  reviewCount: number;
  onReviewCount: (n: number) => void;
  onImport: () => void;
  onChange: () => void;
}): JSX.Element {
  const [params, setParams] = useSearchParams();
  // Fixed at mount, so clearing the queue shows "All sorted" rather than yanking him to the ledger.
  const [fallback] = useState<View>(reviewCount > 0 ? 'review' : 'all');
  const asked = params.get('view');
  const view: View = asked === 'review' || asked === 'all' ? asked : fallback;
  const pick = (v: View): void => setParams({ view: v }, { replace: true });

  return (
    <>
      <div className="hb-seg">
        <Segmented
          label="Transactions view"
          value={view}
          options={[
            { value: 'review', label: 'Needs review', count: reviewCount },
            { value: 'all', label: 'All' },
          ]}
          onChange={pick}
        />
      </div>
      {view === 'review' ? (
        <Review onCount={onReviewCount} />
      ) : (
        <Ledgers onImport={onImport} onChange={onChange} onReview={() => pick('review')} />
      )}
    </>
  );
}

/** The ledger: bank rows by day (tap to re-file) or order lines. */
function Ledgers({ onImport, onChange, onReview }: { onImport: () => void; onChange: () => void; onReview: () => void }): JSX.Element {
  const [view, setView] = useState<'bank' | 'orders'>('bank');
  const [rangeKey, setRangeKey] = useState<RangeKey>('month');
  return (
    <>
      <div className="mn-ledgerbar">
        <Segmented
          label="Ledger source"
          value={view}
          options={[
            { value: 'bank', label: 'Bank' },
            { value: 'orders', label: 'Orders' },
          ]}
          onChange={setView}
        />
        <Button variant="secondary" size="sm" icon="file" onClick={onImport}>
          Import CSV
        </Button>
      </div>
      {view === 'bank' ? (
        <>
          <div className="hb-seg hb-seg--inline mn-range">
            <Segmented label="Date range" value={rangeKey} options={RANGES} onChange={setRangeKey} />
          </div>
          <Ledger rangeKey={rangeKey} onChange={onChange} onReview={onReview} />
        </>
      ) : (
        <Orders />
      )}
    </>
  );
}

const DAY_PAGE = 8;

function Ledger({ rangeKey, onChange, onReview }: { rangeKey: RangeKey; onChange: () => void; onReview: () => void }): JSX.Element {
  const cats = useCategoryTree();
  const range = useMemo(() => rangeOf(rangeKey), [rangeKey]);
  const [rows, setRows] = useState<FinanceTransaction[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<FinanceTransaction | null>(null);
  const [splitting, setSplitting] = useState<FinanceTransaction | null>(null);
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const [days, setDays] = useState(DAY_PAGE);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await getFinanceTransactions({ from: range.from, to: range.to, search: search || undefined, limit: 300 }));
    } finally {
      setLoading(false);
    }
  }, [range, search]);

  useEffect(() => {
    void load();
  }, [load]);

  const toReview = rows.filter((t) => isUncategorized(t.category) && !t.isTransfer).length;
  const byDay = useMemo(() => {
    const groups: { day: string; rows: FinanceTransaction[] }[] = [];
    for (const t of rows) {
      const day = t.postedAt.slice(0, 10);
      const last = groups[groups.length - 1];
      if (last && last.day === day) last.rows.push(t);
      else groups.push({ day, rows: [t] });
    }
    return groups;
  }, [rows]);

  const toggle = (id: string): void =>
    setOpen((o) => {
      const next = new Set(o);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const txnRow = (t: FinanceTransaction): ReactNode => {
    // A split row (0025) shows "Split · 2 categories" and taps open to its lines.
    const split = splitSummary(t);
    const label = split ?? (t.isTransfer && isUncategorized(t.category) ? 'Transfer' : cats.full(t.category));
    const expanded = split !== null && open.has(t.id);
    return (
      <Fragment key={t.id}>
        <Row
          title={t.merchant ?? t.description}
          sub={
            <>
              {label}
              {t.pending && ' · pending'}
            </>
          }
          detail={
            <span className={`hb-amount${t.amountCents > 0 ? ' hb-amount--in' : ''}`}>
              {money(t.amountCents, { sign: t.amountCents > 0 })}
            </span>
          }
          onClick={() => (split ? toggle(t.id) : setEditing(t))}
          swipe={t.amountCents !== 0 ? { trailing: [{ label: 'Split', icon: 'list', tone: 'accent', onAction: () => setSplitting(t) }] } : undefined}
          ariaLabel={
            split
              ? `${t.merchant ?? t.description}, ${money(t.amountCents)}, ${split}. ${expanded ? 'Hide' : 'Show'} lines`
              : `${t.merchant ?? t.description}, ${money(t.amountCents)}, ${label}. Change category`
          }
        />
        {expanded && (
          <li className="ui-swipe">
            <ul className="mn-splitlines" aria-label={`Split lines for ${t.merchant ?? t.description}`}>
              {t.splits!.map((l) => (
                <li key={l.id} className="mn-splitlines__line">
                  <span className="mn-splitlines__cat">
                    {cats.full(l.category)}
                    {l.note && ` · ${l.note}`}
                  </span>
                  <span className="mn-splitlines__amt">{money(l.amountCents, { sign: l.amountCents > 0 })}</span>
                </li>
              ))}
              <li className="mn-splitlines__edit">
                <Button variant="secondary" size="sm" icon="pencil" onClick={() => setSplitting(t)}>
                  Edit split
                </Button>
              </li>
            </ul>
          </li>
        )}
      </Fragment>
    );
  };

  return (
    <>
      <SearchField value={search} onChange={setSearch} placeholder="Search description or merchant" />

      {loading && rows.length === 0 ? (
        <SkeletonGroup />
      ) : rows.length === 0 ? (
        <EmptyState icon="receipt" title="No transactions" hint={search ? 'Nothing matches that search.' : 'Nothing in this window.'} />
      ) : (
        <>
          {!search && toReview > 0 && (
            <Group>
              <Row
                leading={<IconTile name="sort" color="#ff9500" />}
                title={`${toReview} to sort`}
                sub="One at a time, in Needs review"
                onClick={onReview}
                chevron
              />
            </Group>
          )}
          {byDay.slice(0, days).map((g) => (
            <Group key={g.day} header={<span className="mn-day">{dayLabel(g.day)}</span>}>
              {g.rows.map(txnRow)}
            </Group>
          ))}
          {byDay.length > days && (
            <div className="mn-more">
              <Button variant="secondary" onClick={() => setDays((d) => d + DAY_PAGE)}>
                Show earlier days
              </Button>
            </div>
          )}
        </>
      )}

      {editing && (
        // The ledger's picker sheet, with Split as its top-right action.
        <Sheet
          title="Category"
          onClose={() => setEditing(null)}
          className="mn-pickersheet"
          action={
            editing.amountCents !== 0
              ? {
                  label: 'Split',
                  onClick: () => {
                    setSplitting(editing);
                    setEditing(null);
                  },
                }
              : undefined
          }
        >
          <CategoryPickerBody
            txn={editing}
            onPicked={() => {
              setEditing(null);
              void load();
              onChange();
            }}
          />
        </Sheet>
      )}

      {splitting && (
        <SplitEditor
          txn={splitting}
          onClose={() => setSplitting(null)}
          onSaved={(t) => {
            setSplitting(null);
            if (t.splits?.length) setOpen((o) => new Set(o).add(t.id));
            void load();
            onChange();
          }}
        />
      )}
    </>
  );
}

function Orders(): JSX.Element {
  const [rows, setRows] = useState<OrderLine[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const from = new Date(Date.now() - 730 * 86_400_000).toISOString();
      setRows(await getOrders({ from, search: search || undefined }));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <SearchField value={search} onChange={setSearch} placeholder="Search what you bought" />
      {loading && rows.length === 0 ? (
        <SkeletonGroup />
      ) : rows.length === 0 ? (
        <EmptyState
          icon="package"
          title="No order history yet"
          hint={'Import an Amazon or eBay order export. A bank feed says "AMAZON $47.32"; this says what was in the box.'}
        />
      ) : (
        <Group>
          {rows.slice(0, limit).map((o) => (
            <Row
              key={o.id}
              title={o.title}
              sub={`${shortDate(o.orderedAt)}${o.quantity > 1 ? ` · ${o.quantity}×` : ''}`}
              detail={<span className="hb-amount">{money(o.totalCents)}</span>}
            />
          ))}
          <MoreRow count={rows.length - Math.min(limit, rows.length)} onClick={() => setLimit((l) => l + 50)} />
        </Group>
      )}
    </>
  );
}
